import type { Config } from 'tailwindcss';

/**
 * XTND Brand Colors - Shared configuration
 * This configuration contains all the brand colors and design tokens
 * used across the XTND Juniper platform.
 */
export const xtndColors = {
  // Primary XTND Brand Colors
  'xtnd-blue': {
    50: '#f0f9f8',
    100: '#d1ebe8',
    200: '#a3d7d1',
    300: '#76c5bb',
    400: '#5bb3a5',
    500: '#76c5bb', // Primary brand color
    600: '#5aa098',
    700: '#4a8b83',
    800: '#3a6b65',
    900: '#2a4b47',
    DEFAULT: '#76c5bb',
  },
  'xtnd-dark': {
    50: '#f8f9fa',
    100: '#e9ecef',
    200: '#dee2e6',
    300: '#ced4da',
    400: '#6c757d',
    500: '#38435d', // Secondary brand color
    600: '#2f3a52',
    700: '#263047',
    800: '#1d253c',
    900: '#141a31',
    DEFAULT: '#38435d',
  },
  'xtnd-light': {
    DEFAULT: '#d1ebe8',
    50: '#f8fffe',
    100: '#f0f9f8',
    200: '#e8f4f3',
    300: '#d1ebe8',
    400: '#b8ddd8',
    500: '#9fcfc8',
    600: '#86c1b8',
    700: '#6db3a8',
    800: '#54a598',
    900: '#3b9788',
  },
  'xtnd-white': '#ffffff',

  // Semantic color mappings for better DX
  primary: {
    50: '#f0f9f8',
    100: '#d1ebe8',
    200: '#a3d7d1',
    300: '#76c5bb',
    400: '#5bb3a5',
    500: '#76c5bb',
    600: '#5aa098',
    700: '#4a8b83',
    800: '#3a6b65',
    900: '#2a4b47',
    DEFAULT: '#76c5bb',
  },
  secondary: {
    50: '#f8f9fa',
    100: '#e9ecef',
    200: '#dee2e6',
    300: '#ced4da',
    400: '#6c757d',
    500: '#38435d',
    600: '#2f3a52',
    700: '#263047',
    800: '#1d253c',
    900: '#141a31',
    DEFAULT: '#38435d',
  },

  // Background and surface colors for light/dark modes
  background: {
    light: '#ffffff',
    dark: '#1d253c',
    DEFAULT: '#ffffff',
  },
  surface: {
    light: '#f8f9fa',
    dark: '#263047',
    DEFAULT: '#f8f9fa',
  },
  text: {
    primary: {
      light: '#38435d',
      dark: '#d1ebe8',
      DEFAULT: '#38435d',
    },
    secondary: {
      light: '#6c757d',
      dark: '#ced4da',
      DEFAULT: '#6c757d',
    },
  },
};

/**
 * XTND Typography configuration
 */
export const xtndTypography = {
  fontFamily: {
    sans: ['Inter', 'system-ui', 'sans-serif'],
    mono: ['JetBrains Mono', 'Consolas', 'Monaco', 'monospace'],
  },
  fontSize: {
    xs: ['0.75rem', '1rem'] as [string, string],
    sm: ['0.875rem', '1.25rem'] as [string, string],
    base: ['1rem', '1.5rem'] as [string, string],
    lg: ['1.125rem', '1.75rem'] as [string, string],
    xl: ['1.25rem', '1.75rem'] as [string, string],
    '2xl': ['1.5rem', '2rem'] as [string, string],
    '3xl': ['1.875rem', '2.25rem'] as [string, string],
    '4xl': ['2.25rem', '2.5rem'] as [string, string],
    '5xl': ['3rem', '1'] as [string, string],
    '6xl': ['3.75rem', '1'] as [string, string],
  },
};

/**
 * XTND Spacing configuration
 */
export const xtndSpacing = {
  '18': '4.5rem',
  '88': '22rem',
  '128': '32rem',
  'custom-gap': '1.5rem',
};

/**
 * XTND Border radius configuration
 */
export const xtndBorderRadius = {
  xtnd: '0.5rem',
  'xtnd-lg': '0.75rem',
  'xtnd-xl': '1rem',
};

/**
 * XTND Box shadows with brand colors
 */
export const xtndBoxShadow = {
  xtnd: '0 1px 3px 0 rgba(118, 197, 187, 0.1), 0 1px 2px 0 rgba(118, 197, 187, 0.06)',
  'xtnd-md': '0 4px 6px -1px rgba(118, 197, 187, 0.1), 0 2px 4px -1px rgba(118, 197, 187, 0.06)',
  'xtnd-lg': '0 10px 15px -3px rgba(118, 197, 187, 0.1), 0 4px 6px -2px rgba(118, 197, 187, 0.05)',
  'xtnd-xl':
    '0 20px 25px -5px rgba(118, 197, 187, 0.1), 0 10px 10px -5px rgba(118, 197, 187, 0.04)',
};

/**
 * XTND Animations and keyframes
 */
export const xtndAnimations = {
  animation: {
    'fade-in': 'fadeIn 0.5s ease-in-out',
    'fade-in-up': 'fadeInUp 0.5s ease-out forwards',
    'fade-in-right': 'fadeInRight 0.5s ease-out forwards',
    'fade-in-left': 'fadeInLeft 0.5s ease-out forwards',
    'slide-in-left': 'slideInLeft 0.3s ease-out',
    'zoom-in': 'zoomIn 0.5s ease-out forwards',
    'slide-up': 'slideUp 0.3s ease-out',
    'pulse-xtnd': 'pulseXtnd 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    'dot-pulse': 'dot-pulse 1s ease-in-out 0s infinite',
  },
  keyframes: {
    fadeIn: {
      '0%': { opacity: '0' },
      '100%': { opacity: '1' },
    },
    fadeInUp: {
      '0%': { opacity: '0', transform: 'translateY(20px)' },
      '100%': { opacity: '1', transform: 'translateY(0)' },
    },
    fadeInRight: {
      '0%': { opacity: '0', transform: 'translateX(20px)' },
      '100%': { opacity: '1', transform: 'translateX(0)' },
    },
    fadeInLeft: {
      '0%': { opacity: '0', transform: 'translateX(-20px)' },
      '100%': { opacity: '1', transform: 'translateX(0)' },
    },
    slideInLeft: {
      '0%': { opacity: '0', transform: 'translateX(-10px)' },
      '100%': { opacity: '1', transform: 'translateX(0)' },
    },
    zoomIn: {
      '0%': { opacity: '0', transform: 'scale(0.95)' },
      '100%': { opacity: '1', transform: 'scale(1)' },
    },
    slideUp: {
      '0%': { transform: 'translateY(10px)', opacity: '0' },
      '100%': { transform: 'translateY(0)', opacity: '1' },
    },
    pulseXtnd: {
      '0%, 100%': { opacity: '1' },
      '50%': { opacity: '0.8' },
    },
    'dot-pulse': {
      '0%, 80%, 100%': { opacity: '0' },
      '40%': { opacity: '1' },
    },
  },
};

/**
 * XTND Animation CSS styles for use in style tags
 * This provides the CSS keyframes and utility classes as a string
 * that can be injected into components via <style> tags
 */
export const xtndAnimationStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes zoomIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.5s ease-in-out;
  }
  
  .animate-fadeInUp {
    animation: fadeInUp 0.5s ease-out forwards;
  }
  
  .animate-fadeInLeft {
    animation: fadeInLeft 0.5s ease-out forwards;
  }
  
  .animate-fadeInRight {
    animation: fadeInRight 0.5s ease-out forwards;
  }
  
  .animate-slideInLeft {
    animation: slideInLeft 0.3s ease-out;
  }
  
  .animate-zoomIn {
    animation: zoomIn 0.5s ease-out forwards;
  }
`;

/**
 * XTND Responsive breakpoints
 */
export const xtndScreens = {
  xs: '475px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xtnd: '1175px', // Custom XTND breakpoint
  xl: '1280px',
  '2xl': '1536px',
};

/**
 * Base Tailwind configuration for XTND Juniper
 * This can be extended by individual apps and packages
 */
export const baseConfig: Partial<Config> = {
  darkMode: 'class',
  theme: {
    extend: {
      colors: xtndColors,
      ...xtndTypography,
      spacing: xtndSpacing,
      borderRadius: xtndBorderRadius,
      boxShadow: xtndBoxShadow,
      ...xtndAnimations,
      screens: xtndScreens,
    },
  },
  plugins: [],
};

/**
 * Create a Tailwind config with XTND branding
 * @param content - Content paths for the specific app/package
 * @param customConfig - Additional configuration to merge
 */
export function createTailwindConfig(
  content: string[],
  customConfig: Partial<Config> = {}
): Config {
  return {
    content,
    ...baseConfig,
    ...customConfig,
    theme: {
      ...baseConfig.theme,
      ...customConfig.theme,
      extend: {
        ...baseConfig.theme?.extend,
        ...customConfig.theme?.extend,
      },
    },
  };
}

export default baseConfig;